/**
 * BackgroundEngine: the DOM glue around SceneRenderer. It watches the
 * canvas size (ResizeObserver) and device pixel ratio, runs the animation
 * loop only while it is useful, and paces it with a FrameGovernor.
 *
 * - Animating: requestAnimationFrame loop, capped at 60 fps, dropping to a
 *   steady 30 fps on slow devices.
 * - Paused game or hidden tab: the loop stops and the last frame stays up.
 *   Scene time does not advance while stopped, so nothing jumps on resume.
 * - Reduced motion: no loop at all — one static frame, redrawn only when the
 *   theme or size changes.
 */
import type { ThemePack } from '@dropstack/core';
import { FrameGovernor } from './frameGovernor.ts';
import { clamp } from './layout.ts';
import { SceneRenderer } from './renderer.ts';

export interface EngineOptions {
  reducedMotion: boolean;
  paused: boolean;
  /** 0–1 stack tension: quickens ambient motion by up to 15 % and adds a faint warm glow. */
  intensity: number;
}

/** Longest step (s) the scene clock takes in one frame, so a hiccup never makes things lurch. */
const MAX_STEP_S = 0.1;
/** How much faster ambient motion runs at full tension. */
const TENSION_SPEEDUP = 0.15;

export class BackgroundEngine {
  private readonly renderer: SceneRenderer;
  private readonly governor = new FrameGovernor();
  private readonly observer: ResizeObserver | null = null;
  private theme: ThemePack | null = null;
  private opts: EngineOptions = { reducedMotion: false, paused: false, intensity: 0 };
  private width = 0;
  private height = 0;
  private dpr = 1;
  private raf = 0;
  private lastTick: number | null = null;
  /** Scene clock (s). Advances only while animating. */
  private sceneTime = 0;
  private disposed = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    initial: Partial<EngineOptions> = {},
  ) {
    this.opts = { ...this.opts, ...initial, intensity: clamp(initial.intensity ?? 0, 0, 1) };
    this.renderer = new SceneRenderer(canvas);
    this.renderer.setAnimate(!this.opts.reducedMotion);
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver((entries) => {
        const box = entries[entries.length - 1]?.contentRect;
        if (box) this.resize(box.width, box.height);
      });
      this.observer.observe(canvas);
    } else {
      window.addEventListener('resize', this.onWindowResize);
    }
    document.addEventListener('visibilitychange', this.onVisibility);
    const rect = canvas.getBoundingClientRect();
    this.resize(rect.width, rect.height);
  }

  setTheme(theme: ThemePack): void {
    if (this.theme?.id === theme.id) {
      this.theme = theme;
      return;
    }
    // Crossfade only when the player can see it animate; otherwise swap instantly.
    this.renderer.setTheme(theme, this.theme !== null && this.shouldAnimate());
    this.theme = theme;
    this.refresh(true);
  }

  setOptions(next: EngineOptions): void {
    const prev = this.opts;
    this.opts = { ...next, intensity: clamp(next.intensity, 0, 1) };
    if (next.reducedMotion !== prev.reducedMotion) {
      this.renderer.setAnimate(!next.reducedMotion);
      if (next.reducedMotion) this.sceneTime = 0;
    }
    const changed = next.reducedMotion !== prev.reducedMotion || next.paused !== prev.paused || Math.abs(this.opts.intensity - prev.intensity) > 0.001;
    this.refresh(changed);
  }

  dispose(): void {
    this.disposed = true;
    this.stop();
    this.observer?.disconnect();
    window.removeEventListener('resize', this.onWindowResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.renderer.dispose();
  }

  private shouldAnimate(): boolean {
    return !this.disposed && !this.opts.reducedMotion && !this.opts.paused && !document.hidden && this.width > 0 && this.height > 0;
  }

  /**
   * Start or stop the loop to match the current state. With `redraw`, make
   * sure the canvas shows the current state now: a still frame when not
   * animating, or — when animating but the canvas was just cleared by a
   * resize — an immediate frame, so there is never a blank flash.
   */
  private refresh(redraw: boolean): void {
    if (this.shouldAnimate()) {
      if (!this.raf) {
        this.lastTick = null;
        this.governor.resume();
        this.raf = requestAnimationFrame(this.tick);
      }
      if (redraw && !this.renderer.hasFrame) this.draw();
      return;
    }
    this.stop();
    if (redraw) {
      // A still frame never shows a half-finished crossfade.
      this.renderer.finishFade();
      this.draw();
    }
  }

  /**
   * One still frame. Deliberately allowed while the document is hidden: it's a
   * single cheap draw (the loop stays stopped), and it means a page opened in a
   * background tab, a webview that reports "hidden" at launch, or a preview
   * thumbnail shows the scene instead of a bare sky.
   */
  private draw(): void {
    if (!this.theme || this.width <= 0 || this.height <= 0 || this.disposed) return;
    this.renderer.render(this.opts.reducedMotion ? 0 : this.sceneTime, performance.now(), this.opts.intensity);
  }

  private stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  private readonly tick = (now: number): void => {
    this.raf = requestAnimationFrame(this.tick);
    const dpr = window.devicePixelRatio || 1;
    if (dpr !== this.dpr) this.resize(this.width, this.height);
    const dt = this.lastTick === null ? 0 : Math.min(MAX_STEP_S, Math.max(0, (now - this.lastTick) / 1000));
    this.lastTick = now;
    this.sceneTime += dt * (1 + TENSION_SPEEDUP * this.opts.intensity);
    if (!this.governor.shouldDraw(now)) return;
    const t0 = performance.now();
    const baked = this.renderer.render(this.sceneTime, now, this.opts.intensity);
    // Baking the layer cache (new theme or size) is a one-off, not a sign of a slow device.
    if (!baked) this.governor.reportDraw(performance.now() - t0);
  };

  private resize(width: number, height: number): void {
    if (this.disposed) return;
    this.dpr = window.devicePixelRatio || 1;
    this.width = width;
    this.height = height;
    if (width <= 0 || height <= 0) {
      this.stop();
      return;
    }
    this.renderer.resize(width, height, this.dpr);
    // A resize clears the canvas: repaint straight away (see refresh).
    this.refresh(true);
  }

  private readonly onWindowResize = (): void => {
    const rect = this.canvas.getBoundingClientRect();
    this.resize(rect.width, rect.height);
  };

  private readonly onVisibility = (): void => {
    this.refresh(!document.hidden);
  };
}
